import { ReactNode } from "react";
import { Check } from "lucide-react";
import Link from "next/link";
import { Logo } from "@/components/ui/logo";

interface AuthShellProps {
  children: ReactNode;
  title: string;
  subtitle: string;
}

/**
 * Auth Shell - Split-screen layout for authentication pages
 * Left: Branding with mesh gradient (55%)
 * Right: Form content (45%)
 */
export function AuthShell({ children, title, subtitle }: AuthShellProps) {
  return (
    <div className="grid min-h-screen lg:grid-cols-[55%_45%]">
      {/* Left Panel - Branding */}
      <div className="relative hidden overflow-hidden bg-gradient-to-br from-[hsl(168,100%,95%)] via-[hsl(225,60%,95%)] to-[hsl(38,92%,95%)] lg:block">
        {/* Noise overlay */}
        <div className="noise-overlay absolute inset-0" />

        {/* Dot pattern */}
        <div className="absolute inset-0 bg-dots opacity-20" />

        {/* Content */}
        <div className="relative flex h-full flex-col p-12">
          {/* Logo */}
          <Link href="/" className="flex items-center gap-3 group">
            <Logo className="h-12 w-auto" />
          </Link>

          {/* Main content */}
          <div className="flex flex-1 flex-col justify-center max-w-lg">
            <h1 className="font-display mb-6 text-4xl font-normal leading-tight text-primary">
              CAF 4.0 compliance,{" "}
              <span className="italic">made measurable.</span>
            </h1>

            <p className="text-lg text-primary/70 leading-relaxed">
              CAF-Pilot operationalises the NCSC Cyber Assessment Framework for
              UK Critical National Infrastructure — structured scoring, evidence
              mapped to controls, and audit-ready trails that turn months of
              assessment into weeks.
            </p>

            {/* Features list */}
            <ul className="mt-12 space-y-4">
              {[
                "The full CAF 4.0 model, as data.",
                "Measurable scoring and maturity.",
                "Evidence mapped to controls.",
                "Weeks, not months.",
              ].map((feature, i) => (
                <li
                  key={feature}
                  className="flex items-center gap-3 text-primary/80"
                  style={{ animationDelay: `${i * 100}ms` }}
                >
                  <div className="flex h-5 w-5 items-center justify-center rounded-full bg-accent/10">
                    <Check className="h-3 w-3 text-accent" strokeWidth={3} />
                  </div>
                  {feature}
                </li>
              ))}
            </ul>
          </div>

          {/* Footer */}
          <div className="text-sm text-primary/50">
            Built for energy, water, maritime, telecoms &amp; transport
          </div>
        </div>
      </div>

      {/* Right Panel - Form */}
      <div className="flex items-center justify-center p-6 lg:p-12">
        <div className="w-full max-w-[448px]">
          {/* Mobile logo */}
          <div className="mb-8 flex items-center gap-3 lg:hidden">
            <Logo className="h-12 w-auto" />
          </div>

          {/* Page header */}
          <div className="mb-8">
            <h2 className="text-3xl font-bold text-foreground tracking-tight">
              {title}
            </h2>
            <p className="mt-2 text-muted-foreground">
              {subtitle}
            </p>
          </div>

          {/* Form content */}
          {children}
        </div>
      </div>
    </div>
  );
}
